if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        // Udyan 2 Oct: the theme's slider only starts when it has a progress bar, which the photo viewer doesn't have,
        // so 'slideChanged' never fires. Follow the photo list's own scroll instead, so the thumbnail highlight moves
        // with a sideways swipe.
        this.viewerList = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (this.viewerList) {
          this.viewerList.addEventListener('scroll', debounce(this.onViewerScroll.bind(this), 150));
        }
        this.syncThumbnails();
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onViewerScroll() {
        const slides = Array.from(this.viewerList.querySelectorAll('li[data-media-id]')).filter(
          (slide) => slide.clientWidth > 0
        );
        if (!slides.length) return;
        const start = slides[0].offsetLeft;
        let current = slides[0];
        slides.forEach((slide) => {
          if (
            Math.abs(slide.offsetLeft - start - this.viewerList.scrollLeft) <
            Math.abs(current.offsetLeft - start - this.viewerList.scrollLeft)
          )
            current = slide;
        });
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      // Udyan 2 Oct: keep the thumbnails in the same order as the main photos. After a variant switch the main list
      // puts the variant's photo first (global.js updateMedia) but the thumbnails kept the old order, so the old
      // colour stayed first. Photos not in the main list go to the end (variant photos there stay hidden by CSS).
      syncThumbnails() {
        if (!this.viewerList) return;
        const list = this.elements.thumbnails.querySelector('ul');
        if (!list) return;
        const order = Array.from(this.viewerList.querySelectorAll('li[data-media-id]')).map((slide) => slide.dataset.mediaId);
        const items = Array.from(list.querySelectorAll(':scope > li[data-target]'));
        // Udyan 3 Oct: a thumbnail shows only when its photo is in the main list. After a colour switch the old colour's
        // thumbnails used to stay on show (and the new colour's stayed hidden), so the shared photo came second.
        let visibilityChanged = false;
        items.forEach((item) => {
          const hide = !order.includes(item.dataset.target);
          if (item.classList.contains('thumbnail-list_item--variant') !== hide) {
            item.classList.toggle('thumbnail-list_item--variant', hide);
            visibilityChanged = true;
          }
        });
        const sorted = items
          .filter((item) => order.includes(item.dataset.target))
          .sort((a, b) => order.indexOf(a.dataset.target) - order.indexOf(b.dataset.target))
          .concat(items.filter((item) => !order.includes(item.dataset.target)));
        const sameOrder = sorted.every((item, index) => item === items[index]);
        if (!sameOrder) sorted.forEach((item) => list.appendChild(item));
        if ((!sameOrder || visibilityChanged) && typeof this.elements.thumbnails.resetPages === 'function') {
          this.elements.thumbnails.resetPages();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        this.syncThumbnails();
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
